import classNames from 'classnames'
import React, { memo, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'

import styles from './Sparkline.module.scss'

interface SparklineProps {
  prices: number[]
  width?: number
  height?: number
  lineWidth?: number
}

type Coord = { x: number; y: number }

function buildSmoothPath(pts: Coord[]): string {
  if (pts.length < 2) return ''

  const tension = 0.33
  let d = `M ${pts[0].x.toFixed(2)},${pts[0].y.toFixed(2)}`

  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] ?? pts[i]
    const p1 = pts[i]
    const p2 = pts[i + 1]
    const p3 = pts[i + 2] ?? p2

    const cp1x = p1.x + (p2.x - p0.x) * tension
    const cp1y = p1.y + (p2.y - p0.y) * tension
    const cp2x = p2.x - (p3.x - p1.x) * tension
    const cp2y = p2.y - (p3.y - p1.y) * tension

    d += ` C ${cp1x.toFixed(2)},${cp1y.toFixed(2)} ${cp2x.toFixed(2)},${cp2y.toFixed(
      2
    )} ${p2.x.toFixed(2)},${p2.y.toFixed(2)}`
  }

  return d
}

// Normalized (0..1) skeleton points, scaled to the actual width/height at render time.
const SKELETON_POINTS_NORM: Coord[] = [
  { x: 0, y: 0.75 },
  { x: 0.125, y: 0.4 },
  { x: 0.25, y: 0.9 },
  { x: 0.375, y: 0.1 },
  { x: 0.5, y: 0.6 },
  { x: 0.625, y: 0.25 },
  { x: 0.75, y: 0.5 },
  { x: 0.875, y: 0.05 },
  { x: 1, y: 0.35 },
]

export const SparklineSkeleton: React.FC<{
  width?: number
  height?: number
  strokeWidth?: number
}> = memo(({ width = 120, height = 32, strokeWidth = 1.5 }) => {
  const path = useMemo(() => {
    const padding = 3
    const drawW = width - padding * 2
    const drawH = height - padding * 2

    const coords: Coord[] = SKELETON_POINTS_NORM.map((p) => ({
      x: padding + p.x * drawW,
      y: padding + p.y * drawH,
    }))

    return buildSmoothPath(coords)
  }, [width, height])

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={styles.skeleton}
      aria-hidden='true'
    >
      <path
        d={path}
        fill='none'
        stroke='currentColor'
        strokeWidth={strokeWidth}
        strokeLinecap='round'
        strokeLinejoin='round'
        className={styles.skeletonPath}
      />
    </svg>
  )
})

SparklineSkeleton.displayName = 'SparklineSkeleton'

export const Sparkline: React.FC<SparklineProps> = memo(
  ({ prices, width = 120, height = 40, lineWidth = 1.5 }) => {
    const gradientId = useId()
    const lineRef = useRef<SVGPathElement>(null)
    const [pathLength, setPathLength] = useState<number | null>(null)
    const [animationDone, setAnimationDone] = useState(false)

    const { linePath, areaPath, isUp, isFlat } = useMemo(() => {
      if (prices.length < 2) return { linePath: '', areaPath: '', isUp: true, isFlat: false }

      const min = Math.min(...prices)
      const max = Math.max(...prices)
      const range = max - min || 1
      const flat = max === min

      const padding = 3
      const drawW = width - padding * 2
      const drawH = height - padding * 2

      const coords: Coord[] = prices.map((p, i) => ({
        x: padding + (i / (prices.length - 1)) * drawW,
        y: padding + drawH - ((p - min) / range) * drawH,
      }))

      const line = buildSmoothPath(coords)
      const lastPt = coords[coords.length - 1]
      const firstPt = coords[0]
      const area = `${line} L ${lastPt.x.toFixed(2)},${height} L ${firstPt.x.toFixed(
        2
      )},${height} Z`

      return {
        linePath: line,
        areaPath: area,
        isUp: prices[prices.length - 1] >= prices[0],
        isFlat: flat,
      }
    }, [prices, width, height])

    useLayoutEffect(() => {
      if (lineRef.current) {
        setPathLength(lineRef.current.getTotalLength())
        setAnimationDone(false)
      }
    }, [linePath])

    const handleAnimationEnd = () => {
      setAnimationDone(true)
    }

    if (prices.length < 2) return null

    const color = isFlat
      ? 'var(--text-low-em)'
      : isUp
      ? 'var(--text-success-high-em)'
      : 'var(--text-danger-high-em)'

    return (
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className={styles.sparkline}
        aria-hidden='true'
      >
        <defs>
          <linearGradient id={gradientId} x1='0' y1='0' x2='0' y2='1'>
            <stop offset='0%' stopColor={color} stopOpacity={0.2} />
            <stop offset='100%' stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <path
          d={areaPath}
          fill={`url(#${gradientId})`}
          className={classNames(styles.area, { [styles.areaReady]: animationDone })}
        />
        <path
          ref={lineRef}
          d={linePath}
          fill='none'
          stroke={color}
          strokeWidth={lineWidth}
          strokeLinecap='round'
          strokeLinejoin='round'
          className={classNames(styles.line, { [styles.lineReady]: animationDone })}
          style={
            pathLength != null && !animationDone
              ? ({ '--path-length': `${pathLength}` } as React.CSSProperties)
              : undefined
          }
          onAnimationEnd={handleAnimationEnd}
        />
      </svg>
    )
  }
)

Sparkline.displayName = 'Sparkline'
